import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import {
  signInWithPopup, signInWithCustomToken, signOut, onAuthStateChanged,
} from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import {
  ref as dbRef, get, set, update, onValue, query, orderByKey, limitToLast, startAt, endAt,
} from 'firebase/database';
import { auth, db, googleProvider, firebaseConfigured } from '@/firebase';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import { outcomeOf, fingerprintOf, recordFinishedGame } from '@/store/userStats';
import { readMyRating, saveMyRating } from '@/store/ratings';
import {
  key, parseKey, letterAt, clueAt, cellsOf, usableDir, stepCell, advanceAfterType, retreat,
  nextClue, isSolved, wrongCells, answerAt, whiteCells, filledCount, fullNoticeStep, roomSeconds,
  roomPaused, roomClockPatch, IDLE_MS, ACTIVE_WRITE_MS,
} from '@/engine/solving';
import {
  readLocal, writeLocal, readRemote, writeRemote, readAllRemote, flattenLetters, newer, PROGRESS_PATH,
} from '@/store/progress';
import {
  createRoom, findRoomByCode, joinRoom, bindRoom, writeLetter, writeLetters, writePresence, markSolved, sendChat, writeRoomClock,
  noteMyRoom, watchMyRooms,
} from '@/store/rooms';
import {
  freshFromOthers, unreadCount, loadChatRead, saveChatRead,
} from '@/utils/chat';
import {
  createGroup, findGroupByCode, joinGroup, leaveGroup, roomForDay, watchMyGroups, cleanGroupName,
} from '@/store/groups';
import { localDate } from '@/utils/dates';
import { GIANT_PREFIX } from '@/engine/seasons';

// The single place anything is written. Components dispatch; the pure rules
// of movement and solvedness live in engine/solving.js.

const SETTINGS_KEY = 'crossword:settings';
const SAVE_DEBOUNCE_MS = 1200;
export const TIMES_PATH = 'crosswordTimes';

const loadJson = (k) => {
  try { const raw = window.localStorage.getItem(k); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
const saveJson = (k, v) => {
  try { window.localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage off */ }
};

let unwatchStatusFn = null;
let unbindRoomFn = null;
let unbindIndexFn = null;
// A Giant's progress is ~8,000 squares: after the first save only the
// squares that changed are written (cell keys; null = emptied).
let dirtyCells = new Set();
let giantSeeded = false;
let unbindGroupsFn = null;
let unbindMyRoomsFn = null;
let saveTimer = null;
let tickTimer = null;
// The room's filled count on its meta (writeRoomFilled): one pending write
// at a time, at most every ACTIVE_WRITE_MS.
let filledTimer = null;
let filledWrittenAt = 0;
// Solo idleness: when I last tapped or typed, and the clock at that moment
// (what it goes back to if I then sit idle; see IDLE_MS).
let soloActiveAt = 0;
let soloSecondsAtActive = 0;

// Done: solved here, or - in a room - marked solved by anyone, even before
// this phone's copy of the letters has caught up. Nothing more is typed.
const finished = (state) => Boolean(state.solvedAt || state.roomMeta?.solvedAt);

export default createStore({
  state: {
    firebaseConfigured,
    authReady: !firebaseConfigured,
    // Separate from authReady: Firebase Auth can answer before the
    // siteUsers watcher has, and the gate must not flash "waiting for
    // approval" at an approved player in that gap.
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,
    settings: loadJson(SETTINGS_KEY) || {},

    // The archive: date -> { title, weekday, difficulty, words }.
    index: null,
    // The seasonal Giants: giant-<season> -> { title, season, startsOn, squares, ... }.
    giants: {},
    // My solo records by date: { solved, seconds, filled } for the archive marks.
    progressByDate: {},

    // The open puzzle. `game` is the house-facing summary (bug button,
    // auto-update's "is now a safe moment"): non-null while solving.
    puzzle: null,
    puzzleLoading: false,
    puzzleError: null,
    game: null,

    letters: {}, // "r-c" -> { l, by }
    cursor: { row: 0, col: 0, dir: 'across' },
    seconds: 0,
    solvedAt: null,
    reveals: {}, // "r-c": true - letters I was given, not earned
    checked: {}, // "r-c": true - marked wrong by a check, until retyped
    wasFull: false, // every square held a letter at the last look
    fullNotice: false, // "every square is filled, but something isn't right"
    pencil: false,
    timerRunning: false,
    // Stopped for idleness: the play screen covers the puzzle until a tap or key.
    clockPaused: false,

    // Solving together.
    roomId: null,
    roomMeta: null,
    presence: {},
    roomError: null,
    // The room's chat, oldest first (null until the first load), the newest
    // message from someone else that arrived while I was here (the pop-up),
    // and the server time of the last message I've seen.
    chat: null,
    chatPing: null,
    chatReadAt: 0,
    // My standing groups' ids (groups.js); the home screen draws a card each.
    groupIds: [],
    // Rooms I made or joined by code (rooms.js noteMyRoom): { roomId: { date, at } }.
    myRooms: {},
    // Friends' times for a date: { uid: { name, seconds, clean } }.
    times: {},
  },

  getters: {
    myUid: (state) => state.user?.uid || null,
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    isSolved: (state) => Boolean(state.solvedAt),
    isFinished: (state) => Boolean(state.solvedAt),
    isGiant: (state) => Boolean(state.puzzle?.giant),
    inRoom: (state) => Boolean(state.roomId),
    // Per device. On unless switched off in the play menu.
    jumpToNextClue: (state) => state.settings.jumpToNextClue !== false,
    wrapInWord: (state) => state.settings.wrapInWord !== false,
    currentClue: (state) => (state.puzzle
      ? clueAt(state.puzzle, state.cursor.row, state.cursor.col, state.cursor.dir)
      : null),
    // The clue running the other way through the cursor square - shown
    // softly beside the current one, as the NYT app does (Seth, 2026-10-09).
    crossClue: (state) => (state.puzzle
      ? clueAt(state.puzzle, state.cursor.row, state.cursor.col, state.cursor.dir === 'across' ? 'down' : 'across')
      : null),
    currentCells: (state, getters) => (getters.currentClue
      ? cellsOf(getters.currentClue, state.cursor.dir)
      : []),
    filled: (state) => (state.puzzle ? filledCount(state.puzzle, state.letters) : 0),
    totalCells: (state) => (state.puzzle ? whiteCells(state.puzzle).length : 0),
    helpUsed: (state) => Object.keys(state.reveals).length > 0,
    members: (state) => Object.entries(state.roomMeta?.players || {})
      .map(([uid, p]) => ({ uid, ...p }))
      .sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0)),
    others: (state, getters) => Object.entries(state.presence)
      .filter(([uid]) => uid !== getters.myUid)
      .map(([uid, p]) => ({ uid, ...p })),
    myColor: (state, getters) => state.roomMeta?.players?.[getters.myUid]?.color || null,
    chatUnread: (state, getters) => unreadCount(state.chat, getters.myUid, state.chatReadAt),
  },

  mutations: {
    setAuthReady: (state, v) => { state.authReady = v; },
    setUser: (state, user) => { state.user = user; },
    setSiteUser: (state, su) => { state.siteUser = su; state.siteUserReady = true; },
    setSiteUserReady: (state, v) => { state.siteUserReady = v; },
    setSettings: (state, patch) => { state.settings = { ...state.settings, ...patch }; saveJson(SETTINGS_KEY, state.settings); },
    setIndex: (state, index) => { state.index = index; },
    setGiants: (state, giants) => { state.giants = giants || {}; },
    setProgressByDate: (state, map) => { state.progressByDate = map; },
    setProgressSummary: (state, { date, summary }) => { state.progressByDate = { ...state.progressByDate, [date]: summary }; },

    setPuzzleLoading: (state, v) => { state.puzzleLoading = v; },
    setPuzzleError: (state, v) => { state.puzzleError = v; },
    setPuzzle: (state, puzzle) => { state.puzzle = puzzle; },
    setGame: (state, game) => { state.game = game; },
    resetPlay: (state) => {
      state.letters = {};
      state.cursor = { row: 0, col: 0, dir: 'across' };
      state.seconds = 0;
      state.solvedAt = null;
      state.reveals = {};
      state.checked = {};
      state.wasFull = false;
      state.fullNotice = false;
      state.timerRunning = false;
      state.clockPaused = false;
    },
    setWasFull: (state, v) => { state.wasFull = v; },
    setFullNotice: (state, v) => { state.fullNotice = v; },
    setLetters: (state, letters) => { state.letters = letters; },
    setLetter: (state, { k, l, by }) => {
      if (l) state.letters = { ...state.letters, [k]: { l, by } };
      else { const next = { ...state.letters }; delete next[k]; state.letters = next; }
      if (state.checked[k]) { const next = { ...state.checked }; delete next[k]; state.checked = next; }
    },
    setCursor: (state, cursor) => { state.cursor = { ...state.cursor, ...cursor }; },
    setSeconds: (state, v) => { state.seconds = v; },
    setSolvedAt: (state, v) => { state.solvedAt = v; },
    setReveals: (state, v) => { state.reveals = v; },
    markRevealed: (state, k) => { state.reveals = { ...state.reveals, [k]: true }; },
    markChecked: (state, keys) => {
      const next = { ...state.checked };
      for (const k of keys) next[k] = true;
      state.checked = next;
    },
    setTimerRunning: (state, v) => { state.timerRunning = v; },
    setClockPaused: (state, v) => { state.clockPaused = v; },
    setPencil: (state, v) => { state.pencil = v; },

    setRoom: (state, { id, meta }) => {
      if (id !== state.roomId) {
        state.chat = null;
        state.chatPing = null;
        state.chatReadAt = id ? loadChatRead(id) : 0;
      }
      state.roomId = id;
      state.roomMeta = meta ?? null;
    },
    setChat: (state, messages) => { state.chat = messages; },
    setChatPing: (state, message) => { state.chatPing = message; },
    setChatReadAt: (state, at) => { state.chatReadAt = at; },
    setRoomMeta: (state, meta) => { state.roomMeta = meta; },
    setPresence: (state, p) => { state.presence = p || {}; },
    setRoomError: (state, e) => { state.roomError = e; },
    setGroupIds: (state, ids) => { state.groupIds = ids; },
    setMyRooms: (state, rooms) => { state.myRooms = rooms || {}; },
    setTimes: (state, times) => { state.times = times || {}; },
  },

  actions: {
    // ------------------------------------------------------------ auth
    initAuth({ commit, dispatch }) {
      if (!firebaseConfigured || !auth) {
        commit('setAuthReady', true);
        return;
      }
      // The tester URL from `yarn mint-test-token` (scripts/): browser
      // automation cannot click the Google popup, so a minted custom token
      // signs the tester in instead. A no-op without ?testToken=.
      const testToken = new URLSearchParams(window.location.search).get('testToken');
      if (testToken) {
        window.history.replaceState({}, '', window.location.pathname);
        signInWithCustomToken(auth, testToken).catch((error) => {
          console.error('crossword: test token sign-in failed', error);
        });
      }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        if (unbindGroupsFn) { unbindGroupsFn(); unbindGroupsFn = null; commit('setGroupIds', []); }
        if (unbindMyRoomsFn) { unbindMyRoomsFn(); unbindMyRoomsFn = null; commit('setMyRooms', {}); }
        // An anonymous session exists only so a signed-out bug report can
        // carry a token (utils/bugReports.js). It is NOT a solver.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? {
          uid: user.uid, displayName: user.displayName, email: user.email,
        } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => {
            commit('setSiteUser', su);
            if (su?.status === 'approved') { dispatch('loadIndex'); dispatch('watchGroups'); }
          });
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    async signIn({ commit }) {
      if (!auth) throw new Error('Sign-in needs Firebase configured.');
      try {
        markInteractiveSignIn();
        await signInWithPopup(auth, googleProvider);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async signOutOfSite({ commit, dispatch }) {
      await dispatch('closePuzzle');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    // ----------------------------------------------------------- archive
    loadIndex({ commit, state, getters }) {
      const LAST_DATE = '9999-12-31';
      if (!db || unbindIndexFn) return;
      // Dailies only: giant keys sort after every date, so the daily query
      // stops at the last possible date and the Giants are a query of their
      // own. Never endAt('9'): Firebase reads "9" as the integer 9, which
      // sorts before every string key, and the query comes back empty.
      const q = query(dbRef(db, 'crosswordIndex'), orderByKey(), endAt(LAST_DATE), limitToLast(90));
      unbindIndexFn = onValue(q, (snap) => commit('setIndex', snap.val() || {}), (error) => {
        console.error('crossword: index read failed', error);
        commit('setIndex', state.index || {});
      });
      const giants = query(dbRef(db, 'crosswordIndex'), orderByKey(), startAt(GIANT_PREFIX), endAt(`${GIANT_PREFIX}~`));
      onValue(giants, (snap) => commit('setGiants', snap.val() || {}), () => {});
      readAllRemote(getters.myUid).then((all) => {
        const map = {};
        for (const [date, p] of Object.entries(all)) {
          map[date] = { solved: Boolean(p.solvedAt), seconds: p.seconds || 0, filled: Object.keys(p.letters || {}).length, together: Boolean(p.roomId) };
        }
        commit('setProgressByDate', map);
      });
    },

    // ------------------------------------------------------------ open
    async openPuzzle({ commit, dispatch, getters }, date) {
      await dispatch('closePuzzle');
      commit('setPuzzleLoading', true);
      commit('setPuzzleError', null);
      try {
        const snap = await get(dbRef(db, `crosswordPuzzles/${date}`));
        if (!snap.exists()) throw new Error('That puzzle is not here yet.');
        const puzzle = snap.val();
        commit('setPuzzle', puzzle);
        commit('resetPlay');
        // Resume: the newer of the device's copy and the account's.
        const remote = await readRemote(getters.myUid, date);
        const progress = newer(readLocal(date), remote);
        dirtyCells = new Set();
        // Patches only build on the account's copy when it is the newest;
        // otherwise the first save writes the whole record.
        giantSeeded = Boolean(remote) && progress === remote;
        if (progress) {
          const letters = {};
          for (const [k, l] of Object.entries(progress.letters || {})) letters[k] = { l, by: getters.myUid };
          commit('setLetters', letters);
          commit('setSeconds', progress.seconds || 0);
          commit('setSolvedAt', progress.solvedAt || null);
          commit('setReveals', progress.reveals || {});
        }
        dispatch('noteFullness', { initial: true });
        dispatch('placeCursorAtStart');
        commit('setGame', { date, roomId: null, solved: Boolean(progress?.solvedAt) });
        dispatch('watchTimes', date);
        dispatch('startTimer');
      } catch (error) {
        commit('setPuzzleError', error.message);
      } finally {
        commit('setPuzzleLoading', false);
      }
    },

    placeCursorAtStart({ commit, state }) {
      const { puzzle, letters } = state;
      if (!puzzle) return;
      const first = puzzle.across[0];
      const start = nextClue(puzzle, letters, first.row, first.col, 'across', 0, { preferEmpty: true });
      commit('setCursor', start);
    },

    async closePuzzle({ commit, dispatch }) {
      dispatch('stopTimer');
      dispatch('flushRoomFilled');
      if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; await dispatch('saveNow'); }
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: null, meta: null });
      commit('setPresence', {});
      commit('setPuzzle', null);
      commit('setGame', null);
      commit('resetPlay');
    },

    // ----------------------------------------------------------- timer
    startTimer({ commit, dispatch }) {
      if (tickTimer) clearInterval(tickTimer);
      commit('setTimerRunning', true);
      commit('setClockPaused', false);
      // Opening the puzzle counts as a touch.
      dispatch('noteActivity');
      tickTimer = setInterval(() => dispatch('tick'), 1000);
    },
    // Each second. The clock counts only while someone has tapped or typed
    // within IDLE_MS; then it stops and goes back to that last touch.
    tick({ commit, state, dispatch }) {
      if (!state.puzzle || finished(state)) {
        if (state.clockPaused) commit('setClockPaused', false);
        return;
      }
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (state.roomId) {
        if (!state.roomMeta) return;
        commit('setClockPaused', roomPaused(state.roomMeta, now));
        commit('setSeconds', roomSeconds(state.roomMeta, now));
        return;
      }
      if (now - soloActiveAt > IDLE_MS) {
        if (!state.clockPaused) {
          commit('setClockPaused', true);
          commit('setSeconds', soloSecondsAtActive);
          dispatch('scheduleSave');
        }
        return;
      }
      commit('setSeconds', state.seconds + 1);
      if (state.seconds % 15 === 0) dispatch('scheduleSave');
    },
    // A tap or key on the play screen (and opening it): the clock carries
    // on, or starts again from where it stopped. A room's goes to its meta,
    // so one solver's touch keeps it running for everyone.
    noteActivity({ commit, state }) {
      if (!state.puzzle || finished(state)) return;
      const now = Date.now();
      if (state.roomId) {
        const patch = roomClockPatch(state.roomMeta, now);
        if (patch) {
          commit('setRoomMeta', { ...state.roomMeta, ...patch });
          writeRoomClock(state.roomId, patch);
        }
        if (state.clockPaused) {
          commit('setClockPaused', false);
          commit('setSeconds', roomSeconds(state.roomMeta, now));
        }
        return;
      }
      soloActiveAt = now;
      soloSecondsAtActive = state.seconds;
      if (state.clockPaused) commit('setClockPaused', false);
    },
    // How much of a room's grid is filled, for home's cards (Sky, 2026-10-30:
    // "I can't see team progress for giant crossword, only my own"). Written
    // to the room's meta as `filled` / `squares` by whoever has it open, at
    // most every ACTIVE_WRITE_MS and only when it changed; leaving writes
    // the last count at once.
    noteRoomFilled({ dispatch }) {
      if (filledTimer) return;
      const wait = Math.max(0, filledWrittenAt + ACTIVE_WRITE_MS - Date.now());
      filledTimer = setTimeout(() => { filledTimer = null; dispatch('writeRoomFilled'); }, wait);
    },
    flushRoomFilled({ dispatch }) {
      if (!filledTimer) return;
      clearTimeout(filledTimer);
      filledTimer = null;
      dispatch('writeRoomFilled');
    },
    writeRoomFilled({ state }) {
      const { roomId, roomMeta, puzzle } = state;
      if (!roomId || !roomMeta || !puzzle) return;
      const filled = filledCount(puzzle, state.letters);
      const squares = whiteCells(puzzle).length;
      if (roomMeta.filled === filled && roomMeta.squares === squares) return;
      filledWrittenAt = Date.now();
      writeRoomClock(roomId, { filled, squares });
    },
    stopTimer({ commit }) {
      if (tickTimer) clearInterval(tickTimer);
      tickTimer = null;
      commit('setTimerRunning', false);
    },

    // ---------------------------------------------------------- cursor
    selectCell({ commit, state }, { row, col }) {
      const { puzzle, cursor } = state;
      if (!puzzle) return;
      const same = cursor.row === row && cursor.col === col;
      let dir = cursor.dir;
      if (same) dir = dir === 'across' ? 'down' : 'across';
      dir = usableDir(puzzle, row, col, dir);
      commit('setCursor', { row, col, dir });
    },
    toggleDir({ commit, state }) {
      const { puzzle, cursor } = state;
      const want = cursor.dir === 'across' ? 'down' : 'across';
      commit('setCursor', { dir: usableDir(puzzle, cursor.row, cursor.col, want) });
    },
    moveCursor({ commit, state }, { dRow, dCol }) {
      const { puzzle, cursor } = state;
      if (!puzzle) return;
      const dir = dRow ? 'down' : 'across';
      const delta = (dRow || dCol) > 0 ? 1 : -1;
      const [row, col] = stepCell(puzzle, cursor.row, cursor.col, dir, delta);
      commit('setCursor', { row, col, dir: usableDir(puzzle, row, col, dir) });
    },
    jumpClue({ commit, state }, delta) {
      const { puzzle, letters, cursor } = state;
      commit('setCursor', nextClue(puzzle, letters, cursor.row, cursor.col, cursor.dir, delta));
    },
    goToClue({ commit, state }, { entry, dir }) {
      const { letters } = state;
      const cells = cellsOf(entry, dir);
      const empty = cells.find(([r, c]) => !letterAt(letters, r, c)) || cells[0];
      commit('setCursor', { row: empty[0], col: empty[1], dir });
    },

    // ---------------------------------------------------------- typing
    typeLetter({ commit, state, dispatch, getters }, raw) {
      const { puzzle, cursor, letters } = state;
      if (!puzzle || finished(state)) return;
      const l = String(raw || '').toUpperCase();
      if (!/^[A-Z]$/.test(l)) return;
      const k = key(cursor.row, cursor.col);
      // A revealed square stays as given; typing over it is a no-op.
      const opts = { jump: getters.jumpToNextClue, wrap: getters.wrapInWord };
      if (state.reveals[k]) { commit('setCursor', advanceAfterType(puzzle, letters, cursor.row, cursor.col, cursor.dir, opts)); return; }
      commit('setLetter', { k, l, by: getters.myUid });
      dispatch('letterChanged', { k, l });
      commit('setCursor', advanceAfterType(puzzle, state.letters, cursor.row, cursor.col, cursor.dir, opts));
    },
    backspace({ commit, state, dispatch }) {
      const { puzzle, cursor } = state;
      if (!puzzle || finished(state)) return;
      const k = key(cursor.row, cursor.col);
      if (letterAt(state.letters, cursor.row, cursor.col) && !state.reveals[k]) {
        commit('setLetter', { k, l: '' });
        dispatch('letterChanged', { k, l: '' });
        return;
      }
      const back = retreat(puzzle, cursor.row, cursor.col, cursor.dir);
      commit('setCursor', back);
      const kb = key(back.row, back.col);
      if (!state.reveals[kb] && letterAt(state.letters, back.row, back.col)) {
        commit('setLetter', { k: kb, l: '' });
        dispatch('letterChanged', { k: kb, l: '' });
      }
    },
    letterChanged({ state, dispatch, getters }, { k, l }) {
      if (state.roomId) writeLetter(state.roomId, k, l, getters.myUid);
      else { dirtyCells.add(k); dispatch('scheduleSave'); }
      dispatch('checkSolved');
      dispatch('noteFullness');
    },

    // ----------------------------------------------------- check/reveal
    checkCells({ commit, state }, cells) {
      const wrong = wrongCells(state.puzzle, state.letters, cells);
      commit('markChecked', wrong.map(([r, c]) => key(r, c)));
      return wrong.length;
    },
    revealCells({ commit, state, dispatch, getters }, cells) {
      const { puzzle } = state;
      if (!puzzle || finished(state)) return;
      const changes = [];
      for (const [r, c] of cells) {
        const k = key(r, c);
        const answer = answerAt(puzzle, r, c);
        if (letterAt(state.letters, r, c) !== answer) {
          commit('setLetter', { k, l: answer, by: getters.myUid });
          changes.push([k, answer]);
        }
        commit('markRevealed', k);
      }
      if (changes.length) {
        if (state.roomId) writeLetters(state.roomId, changes, getters.myUid);
        else { changes.forEach(([k]) => dirtyCells.add(k)); dispatch('scheduleSave'); }
      }
      dispatch('checkSolved');
      dispatch('noteFullness');
    },
    clearPuzzle({ commit, state, dispatch, getters }) {
      const { puzzle } = state;
      if (!puzzle || finished(state)) return;
      const changes = Object.keys(state.letters).filter((k) => !state.reveals[k]).map((k) => [k, '']);
      for (const [k] of changes) commit('setLetter', { k, l: '' });
      if (state.roomId) writeLetters(state.roomId, changes, getters.myUid);
      else { changes.forEach(([k]) => dirtyCells.add(k)); dispatch('scheduleSave'); }
      dispatch('noteFullness');
      dispatch('placeCursorAtStart');
    },

    // ---------------------------------------------------------- solved
    checkSolved({ commit, state, dispatch }) {
      if (!state.puzzle || state.solvedAt) return;
      // In a room the clock is the room's, so wait for its meta.
      if (state.roomId && !state.roomMeta) return;
      if (!isSolved(state.puzzle, state.letters)) return;
      const at = state.roomMeta?.solvedAt || Date.now();
      commit('setSolvedAt', at);
      commit('setGame', { ...state.game, solved: true });
      if (state.roomId) {
        commit('setSeconds', roomSeconds(state.roomMeta, at));
        if (!state.roomMeta.solvedAt) markSolved(state.roomId);
      }
      dispatch('saveNow');
      dispatch('recordMyStats');
      dispatch('writeMyTime');
    },

    // The grid just became full and isn't right: say so (see fullNoticeStep).
    // In a room every solver hears it, whoever typed the last letter.
    noteFullness({ commit, state }, { initial = false } = {}) {
      if (!state.puzzle) return;
      const full = filledCount(state.puzzle, state.letters) === whiteCells(state.puzzle).length;
      const { wasFull, show } = fullNoticeStep({
        wasFull: state.wasFull, full, solved: Boolean(state.solvedAt), initial,
      });
      commit('setWasFull', wasFull);
      if (show) commit('setFullNotice', true);
      else if (!full || state.solvedAt) commit('setFullNotice', false);
    },

    recordMyStats({ state, getters }) {
      // The Giant stands apart: its weeks-long clock would swamp the daily
      // stats and streaks (it still has its own board, writeMyTime).
      if (state.puzzle?.giant) return;
      const outcome = outcomeOf({
        puzzle: state.puzzle, seconds: state.seconds, reveals: state.reveals, roomMeta: state.roomMeta, myId: getters.myUid,
      });
      if (!outcome || !getters.myUid) return;
      recordFinishedGame({
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({ puzzle: state.puzzle, roomId: state.roomId }),
        outcome,
      });
    },

    writeMyTime({ state, getters }) {
      if (!db || !getters.myUid || !state.puzzle) return;
      const row = {
        name: state.user?.displayName || state.user?.email || 'Someone',
        seconds: state.seconds,
        clean: !getters.helpUsed,
        together: Boolean(state.roomId),
        at: Date.now(),
      };
      set(dbRef(db, `${TIMES_PATH}/${state.puzzle.date}/${getters.myUid}`), row).catch((error) => {
        console.error('crossword: time write failed', error);
      });
    },

    // The solved sheet's difficulty vote (store/ratings.js). Dailies only.
    loadMyRating({ state, getters }) {
      if (!state.puzzle || state.puzzle.giant) return Promise.resolve(null);
      return readMyRating(state.puzzle.date, getters.myUid);
    },
    rateDifficulty({ state, getters }, vote) {
      if (!state.puzzle || state.puzzle.giant) return Promise.resolve();
      return saveMyRating(state.puzzle.date, getters.myUid, {
        vote,
        seconds: state.seconds,
        clean: !getters.helpUsed,
        together: Boolean(state.roomId),
      }).catch((error) => {
        console.error('crossword: rating write failed', error);
      });
    },

    watchTimes({ commit }, date) {
      if (!db) return;
      onValue(dbRef(db, `${TIMES_PATH}/${date}`), (snap) => commit('setTimes', snap.val() || {}), () => {});
    },

    // ----------------------------------------------------------- saving
    scheduleSave({ dispatch }) {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(() => { saveTimer = null; dispatch('saveNow'); }, SAVE_DEBOUNCE_MS);
    },
    // Solo progress only - except that a room's finish is saved as mine
    // too, so the archive marks it solved and reopening shows the grid
    // (Matt, 2026-10-15: a family solve "doesn't appear ... as though I've
    // done it at all"). Mid-room letters still never touch the solo copy.
    async saveNow({ state, getters, commit }) {
      if (!state.puzzle) return;
      if (state.roomId && !state.solvedAt) return;
      const { date } = state.puzzle;
      const progress = {
        letters: flattenLetters(state.letters),
        seconds: state.seconds,
        solvedAt: state.solvedAt,
        reveals: state.reveals,
        updatedAt: Date.now(),
        ...(state.roomId ? { roomId: state.roomId } : {}),
      };
      writeLocal(date, progress);
      commit('setProgressSummary', { date, summary: { solved: Boolean(progress.solvedAt), seconds: progress.seconds, filled: Object.keys(progress.letters).length, together: Boolean(state.roomId) } });
      if (state.puzzle.giant && giantSeeded && !state.roomId && db && getters.myUid) {
        const patch = { seconds: progress.seconds, solvedAt: progress.solvedAt, reveals: progress.reveals, updatedAt: progress.updatedAt };
        for (const k of dirtyCells) patch[`letters/${k}`] = progress.letters[k] || null;
        dirtyCells = new Set();
        await update(dbRef(db, `${PROGRESS_PATH}/${getters.myUid}/${date}`), patch).catch((error) => {
          console.error('crossword: progress save failed', error);
        });
        return;
      }
      dirtyCells = new Set();
      await writeRemote(getters.myUid, date, progress);
      giantSeeded = true;
    },

    // ------------------------------------------------------------ rooms
    async shareRoom({ state, commit, dispatch }) {
      trackEvent('room-shared');
      if (!state.puzzle || state.roomId || !state.user) return null;
      try {
        const { roomId, code } = await createRoom({
          date: state.puzzle.date, user: state.user, letters: state.letters, seconds: state.seconds,
        });
        noteMyRoom(state.user.uid, roomId, state.puzzle.date);
        await dispatch('enterRoom', { roomId });
        return code;
      } catch (error) {
        commit('setRoomError', error.message);
        return null;
      }
    },

    async joinRoomByCode({ commit, dispatch }, input) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        // A group's code opens today's puzzle in that group's room.
        const group = await findGroupByCode(input);
        if (group) { await dispatch('enterGroup', group.groupId); return; }
        const found = await findRoomByCode(input);
        if (!found) throw new Error('No puzzle with that code.');
        await dispatch('enterMyRoom', found.roomId);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    // A room I'm in (or joining by its code), from anywhere: home's
    // "With <friend>" buttons land here too.
    async enterMyRoom({ commit, dispatch, state }, roomId) {
      commit('setRoomError', null);
      try {
        const meta = await joinRoom(roomId, state.user);
        noteMyRoom(state.user.uid, roomId, meta.date);
        if (!state.puzzle || state.puzzle.date !== meta.date) {
          await dispatch('openPuzzle', meta.date);
          if (state.puzzleError) return;
        }
        if (state.roomId === roomId) return;
        await dispatch('enterRoom', { roomId });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    // ----------------------------------------------------------- groups
    watchGroups({ commit, getters }) {
      if (!db || unbindGroupsFn || !getters.myUid) return;
      unbindGroupsFn = watchMyGroups(getters.myUid, (ids) => commit('setGroupIds', ids));
      if (!unbindMyRoomsFn) unbindMyRoomsFn = watchMyRooms(getters.myUid, (rooms) => commit('setMyRooms', rooms));
    },

    // Hop into the group's room for today, making it if I'm first. With
    // `date` a Giant's id, the group's room for that Giant (one a season).
    async enterGroup({ commit, dispatch, state }, arg) {
      const { groupId, date = localDate() } = typeof arg === 'string' ? { groupId: arg } : arg;
      trackEvent('group-entered');
      commit('setRoomError', null);
      try {
        const group = await joinGroup(groupId, state.user);
        if (!state.puzzle || state.puzzle.date !== date) {
          await dispatch('openPuzzle', date);
          if (state.puzzleError) return;
        }
        const roomId = await roomForDay(groupId, group, date, state.user);
        if (state.roomId === roomId) return;
        await joinRoom(roomId, state.user, { color: group.members?.[state.user.uid]?.color });
        await dispatch('enterRoom', { roomId });
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    async startGroup({ commit, state }, name) {
      trackEvent('group-started');
      commit('setRoomError', null);
      const clean = cleanGroupName(name);
      if (!clean || !state.user) return null;
      try {
        return await createGroup({ name: clean, user: state.user });
      } catch (error) {
        commit('setRoomError', error.message);
        return null;
      }
    },

    // The room I'm in becomes a group: same people, same colours, its code
    // (if free) from now on.
    async keepRoomAsGroup({ commit, state }, name) {
      trackEvent('group-kept');
      commit('setRoomError', null);
      const clean = cleanGroupName(name);
      if (!clean || !state.roomId || !state.roomMeta || state.roomMeta.groupId) return null;
      try {
        return await createGroup({ name: clean, user: state.user, fromRoom: { roomId: state.roomId, meta: state.roomMeta } });
      } catch (error) {
        commit('setRoomError', error.message);
        return null;
      }
    },

    async leaveGroup({ commit, getters }, groupId) {
      try {
        await leaveGroup(groupId, getters.myUid);
      } catch (error) {
        commit('setRoomError', error.message);
      }
    },

    enterRoom({ commit, state, dispatch, getters }, { roomId }) {
      dispatch('flushRoomFilled');
      if (unbindRoomFn) unbindRoomFn();
      commit('setRoom', { id: roomId, meta: null });
      commit('setGame', { ...state.game, roomId });
      // In a room the shared letters are the truth; the solo copy is left
      // alone (it is still there if you leave).
      commit('setLetters', {});
      commit('setWasFull', false);
      commit('setFullNotice', false);
      // The room's first batch of letters is a load, not a move.
      let lettersLoaded = false;
      unbindRoomFn = bindRoom(roomId, getters.myUid, {
        onMeta: (meta) => {
          const first = !state.roomMeta;
          commit('setRoomMeta', meta);
          // Presence carries my colour, which only the meta knows - so the
          // first row is written the moment the meta lands, not on the
          // first cursor move.
          if (first) dispatch('publishPresence');
          // Opening the room counts as a touch on its clock.
          if (first) dispatch('noteActivity');
          // Letters that landed first are counted once there's a meta.
          if (first && lettersLoaded) dispatch('noteRoomFilled');
          // Letters may have landed first and been held for the clock.
          if (!state.solvedAt) dispatch('checkSolved');
        },
        onLetters: (letters) => {
          commit('setLetters', letters);
          dispatch('noteRoomFilled');
          dispatch('checkSolved');
          dispatch('noteFullness', { initial: !lettersLoaded });
          lettersLoaded = true;
        },
        onPresence: (p) => commit('setPresence', p),
        onChat: (messages) => {
          const fresh = freshFromOthers(state.chat, messages, getters.myUid);
          commit('setChat', messages);
          if (fresh.length) commit('setChatPing', fresh[fresh.length - 1]);
        },
      });
      dispatch('startTimer');
    },

    leaveRoom({ commit, state, dispatch }) {
      if (!state.roomId) return;
      const { date } = state.puzzle || {};
      dispatch('flushRoomFilled');
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: null, meta: null });
      commit('setPresence', {});
      if (date) dispatch('openPuzzle', date);
    },

    // Not after the solve: nobody's cursor is shown on a finished grid.
    publishPresence({ state, getters }) {
      if (!state.roomId || !getters.myUid || state.solvedAt) return;
      writePresence(state.roomId, getters.myUid, {
        row: state.cursor.row,
        col: state.cursor.col,
        dir: state.cursor.dir,
        name: state.user?.displayName || 'Someone',
        color: getters.myColor,
      });
    },

    sendChat({ state, getters }, text) {
      if (!state.roomId || !getters.myUid) return false;
      return sendChat(state.roomId, {
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || 'Someone',
        color: getters.myColor,
      }, text);
    },

    // Everything in the chat so far has been seen (it's open on screen).
    markChatRead({ commit, state }) {
      const last = state.chat?.[state.chat.length - 1];
      if (!state.roomId || !last?.at || last.at <= state.chatReadAt) return;
      commit('setChatReadAt', last.at);
      saveChatRead(state.roomId, last.at);
    },
  },
});

export { key, parseKey };
